import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilState } from 'recoil';

import { Theme } from '@constants';
import { useNodeHandle } from '@hooks';
import { userSettingsState } from '@store';

import { isAndroid } from '../../../helpers/Platform';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCText } from '../../primitives/TWCText/TWCText';

export const CCToggleButton = forwardRef(
  (
    {
      testID,
      nextFocusUp,
      nextFocusLeft,
      nextFocusRight,
      nextFocusDown,
      showControls,
      accessibilityLabel,
      accessibilityHint,
      accessibilityHintActive,
    },
    reference,
  ) => {
    const [userSettings, setUserSettings] = useRecoilState(userSettingsState);
    const ccEnabled = userSettings.closedCaptionsEnabled;

    const thisHandle = useNodeHandle(reference);
    const noFocus = isAndroid ? thisHandle : null;

    const { t } = useTranslation();
    const tPath = 'videoControls.';

    const [focused, setFocused] = useState(false);

    const onFocusHandler = () => {
      showControls?.();
      setFocused(true);
    };

    const onPressHandler = () => {
      const newSettings = { ...userSettings };

      newSettings.closedCaptionsEnabled = !ccEnabled;
      setUserSettings(newSettings);
      showControls?.();
    };

    return (
      <View style={styles.ccContainer}>
        <TWCButton
          accessibilityHint={ccEnabled ? accessibilityHintActive : accessibilityHint}
          accessibilityLabel={accessibilityLabel}
          height={60}
          nativeID={testID}
          nextFocusDown={nextFocusDown}
          nextFocusLeft={nextFocusLeft ?? noFocus}
          nextFocusRight={nextFocusRight ?? noFocus}
          nextFocusUp={nextFocusUp}
          ref={reference}
          shouldUseContextReference={false}
          styleFocused={ccEnabled ? styles.ccButtonFocus : undefined}
          testID={testID}
          textFocusStyle={ccEnabled ? styles.ccButtonTextFocusOn : styles.ccButtonTextFocusOff}
          textStyle={styles.ccButtonTextFocusOn}
          title={t(`${tPath}ccButtonTitle`)}
          width={84}
          onBlur={() => setFocused(false)}
          onFocus={onFocusHandler}
          onPress={onPressHandler}
        />
        {focused && (
          <TWCText
            importantForAccessibility={'no'}
            style={styles.ccLabelText}
            testID={`${testID}_label`}
            variant={'mediumText18'}
          >
            {ccEnabled ? t(`${tPath}captionsEnabled`) : t(`${tPath}captionsDisabled`)}
          </TWCText>
        )}
      </View>
    );
  },
);

CCToggleButton.propTypes = {
  testID: PropTypes.oneOfType([PropTypes.string, PropTypes.object, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  showControls: PropTypes.func,
  accessibilityLabel: PropTypes.oneOfType([PropTypes.object, PropTypes.string, () => null]),
  accessibilityHint: PropTypes.oneOfType([PropTypes.object, PropTypes.string, () => null]),
  accessibilityHintEnabled: PropTypes.oneOfType([PropTypes.object, PropTypes.string, () => null]),
};

CCToggleButton.defaultProps = {
  testID: null,
  nextFocusUp: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusDown: null,
  showControls: () => {},
  accessibilityLabel: null,
  accessibilityHint: null,
  accessibilityHintEnabled: null,
};

CCToggleButton.displayName = 'CCToggleButton';

const styles = StyleSheet.create({
  ccButtonFocus: {
    backgroundColor: Theme.dodgerLightBlue,
  },
  ccButtonTextFocusOff: {
    color: Theme.colorFocusText,
    fontFamily: 'Roboto-Bold',
    fontSize: Theme.px(30),
  },
  ccButtonTextFocusOn: {
    color: Theme.mainText,
    fontFamily: 'Roboto-Bold',
    fontSize: Theme.px(30),
  },
  ccContainer: {
    alignItems: 'center',
    flexDirection: 'column',
  },
  ccLabelText: {
    marginTop: Theme.px(4),
  },
});
